/**
 * TG-C — pure point-math for the drag-and-drop charging-curve editor.
 *
 * Structurally compatible with `domain/VehicleProfile.ChargingCurvePoint`
 * ({ socPct, powerKw }), but defined locally: `lib/` is a leaf layer and must
 * not import from `domain/` (CLAUDE.md §4.1). The editor (ui) and unit tests
 * use these helpers; the domain's `chargePowerKwAtSoc` interpolation consumes
 * the same shape, so a curve edited here drives power unchanged.
 *
 * All helpers are pure and return fresh arrays. SoC is clamped to [0, 100] and
 * power to [0, maxKw]; the curve is kept sorted ascending by SoC on commit
 * (add/sort), but `movePoint` deliberately preserves array order so a point's
 * index stays stable mid-drag.
 */

export interface CurvePoint {
  readonly socPct: number;
  readonly powerKw: number;
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

/** Clamp a point to the valid editor range: SoC ∈ [0, 100], power ∈ [0, maxKw]. */
export function clampPoint(p: CurvePoint, maxKw: number): CurvePoint {
  const hi = maxKw > 0 ? maxKw : 0;
  return { socPct: clamp(p.socPct, 0, 100), powerKw: clamp(p.powerKw, 0, hi) };
}

/** Round a point to a grid so drag/double-click produce tester-friendly values. */
export function snapPoint(p: CurvePoint, socStep = 1, kwStep = 1): CurvePoint {
  const snap = (v: number, step: number): number =>
    step > 0 ? Math.round(v / step) * step : v;
  return { socPct: snap(p.socPct, socStep), powerKw: snap(p.powerKw, kwStep) };
}

/** Sort ascending by SoC (stable for equal SoC — keeps insertion order). */
export function sortCurve(points: readonly CurvePoint[]): CurvePoint[] {
  return points
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p.socPct - b.p.socPct || a.i - b.i)
    .map(({ p }) => p);
}

/** Add a clamped+snapped point and return the sorted curve. */
export function addPoint(
  points: readonly CurvePoint[],
  p: CurvePoint,
  maxKw: number,
): CurvePoint[] {
  return sortCurve([...points, snapPoint(clampPoint(p, maxKw))]);
}

/** Remove the point at `index` (no-op for an out-of-range index). */
export function removePoint(points: readonly CurvePoint[], index: number): CurvePoint[] {
  if (index < 0 || index >= points.length) return [...points];
  return points.filter((_, i) => i !== index);
}

/**
 * Replace the point at `index` with a clamped value, preserving array order
 * (NOT sorted) so the caller can keep dragging the same index. Sort on commit
 * (pointer-up) via `sortCurve`. No-op for an out-of-range index.
 */
export function movePoint(
  points: readonly CurvePoint[],
  index: number,
  p: CurvePoint,
  maxKw: number,
): CurvePoint[] {
  if (index < 0 || index >= points.length) return [...points];
  const clamped = clampPoint(p, maxKw);
  return points.map((q, i) => (i === index ? clamped : q));
}

/**
 * A "nice" axis ceiling at or above `v`: rounds up to the nearest 10 (≤100) or
 * 50 (>100), with a floor of 10 so an empty/zero curve still has a usable axis.
 */
export function niceCeil(v: number): number {
  if (!Number.isFinite(v) || v <= 10) return 10;
  const step = v <= 100 ? 10 : 50;
  return Math.ceil(v / step) * step;
}
